/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-07 17:58:17
 *
 *******************************************************/
/// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, FieldArray, getFormValues, change } from 'redux-form';
import { FormLayout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import {
  getFilterOptionDisplayTypes_Collection,
  getFilterOptionValueTypes
} from 'src/constants/filter-tree';
import SubCategoryTable from 'src/components/views/filter-tree/item/option-modal/general-tab/collection/sub-category-table';
import { RenderAutocomplete } from 'src/components/views/common/autocomplete-hoc';
import TreeCollections from 'src/components/views/filter-tree/item/option-modal/general-tab/collection/tree-collections';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';

import * as MultiLevelActions from 'actions/filter-tree/multi-level-actions';

type Props = {
  multiLevelTagCollection: boolean
};

type ComposedProps = Props & WithI18nProps;

class Collection extends Component<ComposedProps> {
  render() {
    const { multiLevelTagCollection, i18n } = this.props;

    // Display types
    const displayTypes = getFilterOptionDisplayTypes_Collection(multiLevelTagCollection).map(
      function (type) {
        return {
          ...type,
          label: i18n.translate(type.label)
        };
      }
    );

    const {
      collections,
      collectionDisplayType,
      tags,
      collectionSubCategory,
      collectionMultiLevelManualValues,
      optionType,
      appLanguage,
      collectionOptionValues,
      collectionManualValues,
      collectionExcludedValues
    } = this.props;

    const optionValues = getFilterOptionValueTypes(optionType, appLanguage, i18n);

    return (
      <FormLayout>
        <Field
          name="collectionDisplayType"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Label'
          )}
          options={displayTypes}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Tooltip'
          )}
        />

        {collectionDisplayType === 'list' ? (
          <Field
            name="collectionOptionValues"
            component={RenderSelect}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionValues_Label'
            )}
            options={optionValues}
            tooltip={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionValues_Tooltip'
            )}
          />
        ) : collectionDisplayType === 'sub_category' ? (
          <FieldArray
            name="collectionSubCategory"
            component={SubCategoryTable}
            collections={collections}
            subCategory={collectionSubCategory}
          />
        ) : (
          <TreeCollections
            collectionOptions={collections}
            tagOptions={tags}
            formValues={collectionMultiLevelManualValues}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_TreeCollections_Label'
            )}
            updateManualValues={this.updateManualValues}
          />
        )}

        {collectionDisplayType === 'list' && collectionOptionValues === 'specific' && (
          <Field
            name={'collectionManualValues'}
            component={RenderAutocomplete}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_ManualValues_Label'
            )}
            value={collectionManualValues}
            options={collections}
            placeholder={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SelectThreeDots'
            )}
            extraSearchFields={['value', 'handle']}
          />
        )}

        {collectionDisplayType === 'list' && collectionOptionValues === 'all' && (
          <Field
            name="collectionExcludedValues"
            component={RenderAutocomplete}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_ExcludedValues_Label'
            )}
            value={collectionExcludedValues}
            options={collections}
            placeholder={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SelectThreeDots'
            )}
            tooltip={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_ExcludedValues_Tooltip'
            )}
            extraSearchFields={['value', 'handle']}
          />
        )}

        <Field
          name="collectionKeepValuesStatic"
          component={RenderCheckbox}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_Field3_Label'
          )}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_Field3_Tooltip'
          )}
        />
        <Field
          name="collectionActiveCollectionAll"
          component={RenderCheckbox}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_Field4_Label'
          )}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_Field4_Tooltip'
          )}
        />
      </FormLayout>
    );
  }

  updateManualValues = updatedList => {
    // Dispatch action to update manual values tag
    this.props.updateManualValues(updatedList);
  };
}

const mapDispatchToProps = dispatch => ({
  updateField: (field, value) => dispatch(change(`OptionModalForm`, field, value)),
  updateManualValues: manualValues =>
    dispatch(MultiLevelActions.updateManualValuesMultiLevelCollections(manualValues))
});

const mapStateToProps = (state, props) => {
  const formFilterOptionValues = getFormValues('OptionModalForm')(state);
  const multiLevelCollections = _.get(state.multiLevelFilterOption, 'multiLevelCollections', {});

  return {
    tags: _.get(state.filterData.filterData, 'tags', []),
    collections: _.get(state.filterData.filterData, 'collections', []),
    collectionDisplayType: _.get(formFilterOptionValues, 'collectionDisplayType', ''),
    collectionSubCategory: _.get(formFilterOptionValues, 'collectionSubCategory', [{}]),
    collectionMultiLevelManualValues: _.get(
      multiLevelCollections,
      'collectionMultiLevelManualValues',
      []
    ),
    collectionOptionValues: _.get(formFilterOptionValues, 'collectionOptionValues', ''),
    collectionManualValues: _.get(formFilterOptionValues, 'collectionManualValues', []),
    collectionExcludedValues: _.get(formFilterOptionValues, 'collectionExcludedValues', []),
    optionType: _.get(formFilterOptionValues, 'optionType', ''),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(Collection));
